/**
 * PebbleShape — decorative biomorphic image container.
 *
 * Renders an image (or colour fill) inside an organic SVG clip-path.
 * Shape paths extracted from Figma design (node 1:48) for exact match.
 *
 * 6 shape variants:
 * - blob-1/2/3: organic blob clip-paths for photo pebbles
 * - round-1/2/3: organic rounded clip-paths for solid-colour elements
 */

import { useId } from 'react'

export type PebbleVariant = 'blob-1' | 'blob-2' | 'blob-3' | 'round-1' | 'round-2' | 'round-3'

interface PebbleShapeProps {
  /** Image source URL */
  src?: string
  /** Alt text for the image */
  alt?: string
  /** Shape variant — different organic blob shapes */
  variant?: PebbleVariant
  /** Size class (Tailwind w/h classes) */
  size?: string
  /** Optional CSS class overrides */
  className?: string
  /** Background colour when no image provided */
  bgColor?: string
  /** Called when the image fails to load */
  onError?: () => void
}

/**
 * SVG blob shape definitions from Figma node 1:48.
 * Each has a viewBox and a path `d` attribute.
 *
 * - round-1/2/3: extracted directly from Figma SVG exports
 * - blob-1/2/3: organic ovals for photo masking, derived from Figma masks
 */
export const blobShapes: Record<PebbleVariant, { viewBox: string; d: string }> = { // eslint-disable-line react-refresh/only-export-components
  // Photo blob — wide horizontal oval (top-left photo, node 1:49)
  'blob-1': {
    viewBox: '0 0 472 420',
    d: 'M236 0C367 0 472 94 472 210S367 420 236 420S0 326 0 210S105 0 236 0Z',
  },
  // Photo blob — tall vertical oval (top-right photo, node 1:54)
  'blob-2': {
    viewBox: '0 0 380 462',
    d: 'M190 0C295 0 380 103 380 231S295 462 190 462S0 359 0 231S85 0 190 0Z',
  },
  // Photo blob — round (bottom-left photo, node 1:61)
  'blob-3': {
    viewBox: '0 0 414 414',
    d: 'M207 0C321 0 414 93 414 207S321 414 207 414S0 321 0 207S93 0 207 0Z',
  },
  // Solid teal — organic teardrop (node 1:59, exact Figma path)
  'round-1': {
    viewBox: '0 0 211 257',
    d: 'M76.9758 251.161C-0.641203 220.977 -23.7867 99.2358 27.0491 35.7259C76.8785 -27.8487 165.598 0.068437 196.112 67.3675C220.361 117.113 215.297 182.016 178.647 223.6C154.203 252.165 113.366 265.281 77.8524 251.485L76.9758 251.161Z',
  },
  // Solid green — organic blob (node 1:60, exact Figma path)
  'round-2': {
    viewBox: '0 0 228 268',
    d: 'M127.808 267.923C38.2411 263.662 -23.4933 149.717 8.59466 67.6987C43.0543 -26.8245 151.735 -16.2754 200.285 61.6906C252.114 134.207 232.792 269.111 129.273 267.993H127.808V267.923Z',
  },
  // Solid light green — organic round (node 1:71, exact Figma path)
  'round-3': {
    viewBox: '0 0 166 167',
    d: 'M87.97 167C27.0427 166.681 -17.9579 95.4289 7.05434 40.4723C26.2945 -4.04686 84.1219 -10.6502 121.854 14.8045C191.653 58.7912 177.544 165.722 88.6113 167H87.97Z',
  },
}

export function PebbleShape({
  src,
  alt = '',
  variant = 'blob-1',
  size = 'w-32 h-32',
  className = '',
  bgColor,
  onError,
}: PebbleShapeProps) {
  const id = useId()
  const clipId = `pebble-clip-${id}`
  const shape = blobShapes[variant]
  const [,, vbW, vbH] = shape.viewBox.split(' ')
  const scaleX = 1 / Number(vbW)
  const scaleY = 1 / Number(vbH)

  return (
    <div className={`${size} relative ${className}`} aria-hidden={!alt}>
      {/* Hidden SVG defining the clip-path shape */}
      <svg
        className="absolute w-0 h-0"
        aria-hidden="true"
      >
        <defs>
          <clipPath id={clipId} clipPathUnits="objectBoundingBox">
            <path
              d={shape.d}
              transform={`scale(${scaleX}, ${scaleY})`}
            />
          </clipPath>
        </defs>
      </svg>

      {/* Clipped content */}
      <div
        className="w-full h-full"
        style={{ clipPath: `url(#${clipId})` }}
      >
        {src ? (
          <img
            src={src}
            alt={alt ?? ''}
            aria-hidden={alt ? undefined : 'true'}
            className="w-full h-full object-cover transition-opacity duration-500"
            loading="lazy"
            onError={onError}
          />
        ) : (
          <div
            className="w-full h-full"
            style={{ backgroundColor: bgColor || 'var(--color-light-green)' }}
          />
        )}
      </div>
    </div>
  )
}
